// Utils
import { get, post, put, upload, destroy } from 'app/shared/utils/fetchr';

// Models
import {
  AddDocumentsPayload,
  ChangeAlertStatusPayload,
  CreateAlertPayload,
  DeleteDocumentPayload,
} from 'app/modules/alerts/requests';
import { BulkFileExportPayload } from 'app/modules/fileExportsOld/models';
import { PaginationPayload } from 'app/shared/pagination/models';

// Actions
import {
  editAlert,
  retrieveAlertActionEvents as retrieveAlertActionEventsAction,
} from 'app/modules/alerts/actions';

// Utils
import routes, { pathJoin } from 'app/shared/utils/routes';
import { removeNullKeys } from 'app/shared/utils/apiUtils';
import { UploadFilesPayload } from 'app/modules/uploads/models';
import { RuleValidationAlertActionEventsResponse } from 'app/modules/rules/models';
import { ShortInstrumentResponse } from 'app/modules/instruments/responses';
import { DeleteAlertCaseAttachmentResponse } from 'app/modules/attachments/responses';

export const updateAlert = (
  payload: ReturnType<typeof editAlert>['payload'],
) => {
  const { id, ...details } = payload;
  return put(routes.patronus.alertsEditId.replace(':id', String(id)), details);
};

export const createAlert = (payload: CreateAlertPayload) => {
  return post(routes.patronus.alertsCreate, payload);
};

export const getAlert = (alertId: number | string) => {
  const path = routes.patronus.alertsAlertId.replace(
    ':alertId',
    String(alertId),
  );
  return get(path);
};

export const getAlertTransactions = (
  id: string,
  payload: PaginationPayload,
) => {
  const path = routes.patronus.alertsIdTransactions.replace(':id', id);
  return post(path, payload);
};

export const getAlertTransactionsFromCache = (
  id: string,
  payload: PaginationPayload,
) => {
  return post(`/v1/alerts/cached/${id}/transactions`, payload);
};

export const retrieveAlertEntities = (id: string, payload) => {
  const path = routes.patronus.alertsIdEntities.replace(':id', id);
  return post(path, payload);
};

export const retrieveAlertInstruments = (id: string, payload) => {
  const path = routes.patronus.alertsIdInstruments.replace(':id', id);
  return post<{ instruments: ShortInstrumentResponse[] }>(path, payload);
};

export const alertComponentStatusChange = (
  payload: ChangeAlertStatusPayload,
) => {
  const { alertId, ...details } = payload;
  const path = routes.patronus.alertsIdComponentResolution.replace(
    ':id',
    `${alertId}`,
  );
  return post(path, details);
};

export const addDocuments = (payload: AddDocumentsPayload) => {
  const { alertId, documents } = payload;
  const docs = documents.map((doc) => removeNullKeys(doc));

  return post(pathJoin('/v1/alerts', alertId, '/attachments', 'add'), {
    docs,
  });
};

export const uploadFile = (payload: UploadFilesPayload) => {
  const formData = new FormData();

  payload.files?.forEach((S3File, index) => {
    formData.append(`file${index}`, S3File);
  });

  return upload(
    routes.patronus.alertsIdAddFiles.replace(':id', payload.id),
    formData,
  );
};

export const deleteDocument = (payload: DeleteDocumentPayload) => {
  const { alertId, docId } = payload;
  const alertPath = routes.patronus.alertsIdAttachmentId.replace(
    ':id',
    alertId.toString(),
  );
  const fullPath = alertPath.replace(':attachmentId', docId.toString());
  return destroy<DeleteAlertCaseAttachmentResponse>(fullPath);
};

export const sendBulkAlertsXlsxRequest = (payload: BulkFileExportPayload) => {
  return post(routes.patronus.alertsBulkXlsx, payload);
};

export const retrieveAlertActionEvents = ({
  alert_id: id,
  ...payload
}: ReturnType<typeof retrieveAlertActionEventsAction>['payload']) => {
  const path = routes.patronus.alertsIdActionEvents.replace(':id', `${id}`);
  return post<RuleValidationAlertActionEventsResponse>(path, payload);
};
